import React, {
  lazy,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';

import hive from '@bombas/hive';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { ApplicationContext } from 'shop/context/ApplicationContext';
import { PRODUCT_PAGE } from './Product.gql';
import Loader from '../Loader/Loader';
import ProductProvider, { ProductContext } from './Product.context';
import * as Styled from './Product.styled';
import ProductReviews from './components/ProductReviews/ProductReviews';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { useQuickView } from '../QuickView/QuickView.context';
import BreakpointProvider from '../../util/Breakpoint.context';
import { ContainerQueryContext } from '../../util/ContainerQuery.context';
import { trackEvent as dataLayerPush } from '../../util/track';
import ProductFullDetailsModal from './components/ProductFullDetailsModal/ProductFullDetailsModal';
import ProductPropTypes from './Product.propTypes';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { SITE_CONFIG } from '../App.gql';
import { isPreview } from '../../graphql/previews';
import { mapOldFieldsToNewComponentValues } from './Product.utils.deprecated';
import { getProductCallouts } from './Product.utils';
import ProductMicrodata from './components/ProductMicrodata/ProductMicrodata';

const CardContainer = lazy(() => import('../CardContainer/CardContainer.pim'));
const CardSection = lazy(() =>
  import('../ContentSystem/components/CardSection/CardSection')
);
const ContentBlock = lazy(() => import('../ContentBlock/ContentBlock.pim'));
const HeaderSection = lazy(() =>
  import('../ContentSystem/components/HeaderSection/HeaderSection')
);
const ImageSection = lazy(() =>
  import('../ContentSystem/components/ImageSection/ImageSection')
);
const TextBlock = lazy(() =>
  import('../ContentfulTextBlock/ContentfulTextBlock.pim')
);
const TextSection = lazy(() =>
  import('../ContentSystem/components/TextSection/TextSection')
);
const ResponsiveContainer = lazy(() =>
  import('../ResponsiveContainer.pim/ResponsiveContainer')
);

/**
 * A `Product` component is used to display a product's details and images.
 */
const Product = ({ product, ...props }) => {
  const router = useRouter();

  const imageContainerRef = useRef(null);
  const {
    isQuickView,
    setContainerStylesApplied,
    setActiveProductId,
    previousUrl
  } = useQuickView();
  const { styleQueryMode, containerQueryClassName } = useContext(
    ContainerQueryContext
  );
  const { pdpCalloutsConfig } = useContext(ApplicationContext);

  const [isFullDetailsOpen, setIsFullDetailsOpen] = useState(false);

  const handleShowFullProductDetails = useCallback(() => {
    if (isQuickView) {
      hive.track('PRODUCT_DETAILS_QUICKVIEW_CLICK');
    } else {
      hive.track('PRODUCT_DETAILS_CLICK');
    }
    setIsFullDetailsOpen(true);
  }, [setIsFullDetailsOpen]);

  // filter out any colors that don't have a URL
  const swatches = useMemo(
    () => product.colors.filter((c) => c.swatch.file.url),
    [product.colors]
  );

  const callouts = useMemo(() => {
    if (pdpCalloutsConfig?.length > 0) {
      return getProductCallouts({
        productData: product,
        attributeSettings: pdpCalloutsConfig
      });
    }
    return [];
  }, [pdpCalloutsConfig, product]);

  useEffect(() => {
    if (!isQuickView) {
      dataLayerPush({ event: 'initPdpType', pdp_type: 'pim' });
    }

    if (window.yotpoWidgetsContainer) {
      // Defer execution since initial full-page render has not yet completed
      setTimeout(() => {
        // Ensures the Yotpo modules are updated without requiring a page reload.
        window.yotpoWidgetsContainer.initWidgets();
      }, 100);
    }
  }, []);

  useEffect(() => {
    /**
     * The container query library calculates container size after loading, causing layout shift because PDP gets restyled as its rendered
     *
     * To minimize layout shift, wait for container query sizes and then tell quickview provider that PDP has those styles
     */
    if (containerQueryClassName && isQuickView) {
      setContainerStylesApplied(true);
    }
    return () => {
      if (containerQueryClassName && isQuickView) {
        setContainerStylesApplied(false);
      }
    };
  }, [containerQueryClassName]);

  const currency = product?.variants.find((v) => v.price)?.price.currency;

  /**
   * Sends a viewProduct event with product data to Google Tag Manager (GTM)
   *
   * @remarks
   *
   * Many tags in GTM are triggered by the viewProduct custom event and require
   * pieces of product data including product's Shopify ID, title, type/category, price,
   * and product image URLs.
   *
   * Shopify ID, title, and type/category data is available on the product object received
   * from graphQL. Price and image data must be derived from the Variants for the product.
   *
   * Once the necessary data is collected, it is compiled together in an object under a
   * json property. The data is structured to match the legacy implementation of
   * the viewProduct event from the gtm-data-layer.liquid file.
   * {@link https://github.com/bombas/bombas/blob/main/snippets/gtm-data-layer.liquid}
   */
  useEffect(() => {
    const availableVariants =
      product?.variants?.filter(
        (variant) => variant.available || variant.replenishable
      ) || [];

    /**
     * Aggregation of all product image host URLs into a Set.
     *
     * @remarks
     *
     * The resulting Set of image URLs are collected from every Variant object
     * that is available or replenishable (aka a core item). Every new image
     * URL encounted in the Variants will be added to the Set.
     *
     * Variant objects related to the same color usually have the same
     * images with the same host URLs. Since many Variants have the same
     * images and host URLs, a Set is used to de-dupe the image URLs
     * collected from the Variant objects. Using a Set to de-dupe
     * the data decreases the size/length of the image data collected and
     * sent to GTM.
     *
     * @returns Set of product image host URLs.
     *
     * @param accum - Set storing strings for each unique image URL
     * @param variant - Variant object received from graphQL
     */
    const productImages = availableVariants.reduce((accum, variant) => {
      variant.images.forEach((image) => {
        const { url } = image.image.file;
        accum.add(url);
      });
      return accum;
    }, new Set());

    /**
     * Price data must be derived from the Variants. All Variants of a Product
     * usually have the same price though it is possible for different Variants
     * to have different prices. At this stage, it is not known what Variant
     * will be the currently selected Variant when the PDP loads
     *
     * The first available or replenishable Variant object received from graphQL
     * is used to determine the product's price. If the Variant has a discount price,
     * that data is used instead of base price.
     */
    const leadVariant = availableVariants[0];
    const discountPrice =
      leadVariant?.price?.discountPrice &&
      Number(leadVariant.price.discountPrice) * 100;
    const basePrice =
      leadVariant?.price?.basePrice &&
      Number(leadVariant.price.basePrice) * 100;
    const shopifyId =
      product?.externalSystems?.shopify &&
      Number(product.externalSystems.shopify);
    const productImagesArray = Array.from(productImages);
    const leadProductImageURL = `https:${productImagesArray[0]}`;

    const productData = {
      json: {
        id: shopifyId,
        title: product.title,
        type: product?.categories?.join('-') || '',
        price: discountPrice || basePrice,
        currency,
        images: productImagesArray,
        leadProductImageUrl: leadProductImageURL
      }
    };

    hive.track('PRODUCT_VIEW', {
      product: productData
    });
  }, []);

  return (
    <ErrorBoundary>
      <ProductProvider {...product} colors={swatches}>
        <BreakpointProvider>
          <ProductContext.Consumer>
            {({ currentVariant }) => (
              <>
                {
                  <Styled.Product
                    $styleQueryMode={styleQueryMode}
                    id="react-product"
                  >
                    <ProductMicrodata
                      title={product.title}
                      description_html={product.description}
                      sku={currentVariant.sku}
                      price={currentVariant.price?.basePrice}
                      currency={currentVariant.price?.currency}
                      color={currentVariant.color?.title}
                      size={currentVariant?.sizes?.value}
                      images={currentVariant.images}
                      isAvailable={currentVariant.available}
                    />
                    <Styled.ProductDetailsContainer
                      data-cnstrc-product-detail
                      data-cnstrc-item-id={product.externalSystems?.shopify}
                      data-cnstrc-item-variation-id={
                        currentVariant.externalSystems?.shopify
                      }
                      data-cnstrc-item-name={product.title}
                      data-cnstrc-item-price={currentVariant.price?.price}
                    >
                      <Styled.StickyBar
                        topOffset={styleQueryMode === 'screen' ? '80px' : '0'}
                        withStickyStyle={false}
                        enabled={{
                          small: false,
                          medium: true,
                          large: true
                        }}
                      >
                        <Styled.ProductDetailsInner
                          data-label="productDetails"
                          data-testid="ProductDetails"
                          $styleQueryMode={styleQueryMode}
                        >
                          <Styled.Breadcrumbs
                            dataLabel="breadcrumbs_pdp"
                            crumbs={product.breadcrumbs}
                          />
                          <Styled.ProductDetails
                            callouts={callouts}
                            colors={swatches || []}
                            variants={product.variants}
                            product_type={currentVariant.category}
                            gender={currentVariant.genders}
                            id={product.externalSystems.shopify}
                            sizes={product.sizes}
                            title={product.title}
                            shopifyId={product.externalSystems.shopify}
                            onShowFullDetails={handleShowFullProductDetails}
                            guides={product.sizeGuideInputs}
                            categories={product.categories}
                            {...props}
                            relatedProducts={product.relatedProducts}
                            url={product.url}
                            images={currentVariant.images}
                            onAddToBag={
                              // Safely call addToCart function
                              (variantId, quantity = 1) => {
                                if (!isQuickView) {
                                  hive.track('CART_ADD_PDP');
                                } else {
                                  hive.track('CART_ADD_PCP');
                                }

                                // Closes QuickView modal when a product is added to cart from the QuickView modal
                                // After that, we update the URL back to the PCP and remove the added history from the stack
                                if (isQuickView) {
                                  hive.track('CART_ADD_QUICKVIEW');

                                  setActiveProductId(null);
                                  setTimeout(() => {
                                    router.replace(previousUrl, '', {
                                      scroll: false,
                                      shallow: true
                                    });
                                    router.back();
                                  }, 400);
                                }

                                PubSub.publish('cart.add', {
                                  variantId,
                                  quantity,
                                  productId: product.externalSystems.shopify,
                                  pimData: {
                                    category: currentVariant.categories,
                                    isPack: currentVariant.itemCount > 1,
                                    packSize: currentVariant.itemCount,
                                    endUse: currentVariant.components[0].endUse,
                                    endUse2:
                                      currentVariant.components[0].endUse2,
                                    lifestyle:
                                      currentVariant.components[0].lifestyle,
                                    material:
                                      currentVariant.components[0]
                                        .primaryMaterial
                                  },
                                  source: isQuickView ? 'QuickView' : 'PDP'
                                });
                              }
                            }
                          />
                        </Styled.ProductDetailsInner>
                      </Styled.StickyBar>
                    </Styled.ProductDetailsContainer>
                    <Styled.ProductImageContainer ref={imageContainerRef}>
                      <Styled.ResponsiveImageGallery
                        images={currentVariant.images}
                        $styleQueryMode={styleQueryMode}
                      />
                    </Styled.ProductImageContainer>
                  </Styled.Product>
                }
                <ProductFullDetailsModal
                  isOpen={isFullDetailsOpen}
                  onRequestClose={() => setIsFullDetailsOpen(false)}
                  description_html={product.description}
                  details={product.details}
                  callouts={callouts}
                />
              </>
            )}
          </ProductContext.Consumer>
        </BreakpointProvider>
      </ProductProvider>
    </ErrorBoundary>
  );
};

/**
 * `Product` component wrapped with data fetching.
 */
const ProductWithData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const preview = urlParams.get('preview') === 'page';
  const errorPolicy = urlParams.get('gql_error') === 'all' ? 'all' : 'none';
  const [pageHas404Container] = useState(
    document.querySelector('body.template-404')
  );
  const { handle, isQuickView, setApolloError } = useQuickView();

  // QuickView does not update the URL to the product page, so we would have to use QuickView context to get the handle
  // and pass it in to the query.
  // If the user is not in QuickView, we can use the URL to get the handle
  const path =
    handle || (window ? window.location.pathname.replace(/(\/)+$/, '') : '');

  // Block rendering and data fetchting for shopify draft products
  if (pageHas404Container && !preview) {
    return <NotFoundPage />;
  }

  const { data, error } = useQuery(PRODUCT_PAGE, {
    context: {
      headers: {
        'X-Preview': isPreview('page')
      },
      withCacheKey: true
    },
    variables: {
      preview: isPreview('page'),
      path
    },
    errorPolicy,
    // onError does not execute with 'all' error policy
    onError: (err) => {
      window.DD_RUM?.onReady(() => {
        if (err.networkError) {
          window.DD_RUM?.addError(
            (new Error(err.networkError),
            {
              location: 'PDP',
              type: 'network',
              PDP_type: 'pim'
            })
          );
        }

        err.graphQLErrors.forEach((e) => {
          window.DD_RUM?.addError(new Error(e), {
            location: 'PDP',
            type: 'graphQL',
            PDP_type: 'pim'
          });
        });
      });
    }
  });

  const { data: settingsData } = useQuery(SITE_CONFIG, {
    variables: { key: 'pdp', preview: isPreview('page') }
  });
  const config = settingsData?.settings[0].values;
  const { styleQueryMode } = useContext(ContainerQueryContext);
  let reviewsComponent = null;

  // If Quickview is open and there is an apollo error, set the state in the context to render
  // the error message in the quickview modal
  useEffect(() => {
    if (isQuickView && error && errorPolicy === 'none') {
      setApolloError(error);
    }
  }, [error]);

  if (!isQuickView && error && errorPolicy === 'none') {
    return <NotFoundPage />;
  }

  if (!data || !config) {
    return null;
  }

  const { page } = data;

  return (
    <ErrorBoundary>
      <>
        {page.sections?.map((module) => {
          const blocks = {
            CardContainer,
            ContentBlock,
            Product,
            ResponsiveContainer,
            TextBlock,
            CardSection,
            HeaderSection,
            ImageSection,
            TextSection
          };

          const Module = blocks[module.__typename];

          if (Module && module.__typename === 'Product') {
            reviewsComponent = (
              <ProductReviews
                id={module.externalSystems.shopify || undefined}
                title={module.title}
                containerQueryMode={styleQueryMode}
              />
            );

            return <Product key="product" product={module} config={config} />;
          }

          if (Module) {
            const newComponentValues = mapOldFieldsToNewComponentValues(module);

            return (
              <Loader key={module.id || module.title}>
                <Module
                  {...module}
                  {...newComponentValues}
                  styleQueryMode={styleQueryMode}
                />
              </Loader>
            );
          }

          return null;
        })}
        {reviewsComponent}
      </>
    </ErrorBoundary>
  );
};

ProductWithData.displayName = 'ProductWithData';

Product.propTypes = {
  product: PropTypes.shape(ProductPropTypes).isRequired
};

Product.displayName = 'Product';

export { Product };
export default ProductWithData;
